import { useEffect, useState } from 'react';
import api from '../services/api';
import { lerCrecheSelecionada } from '../services/sessao';

export interface PlanoDaCreche {
  /** 'BRONZE' | 'PRATA' | 'OURO'. `null` = ainda carregando ou sem creche. */
  plano: string | null;
  status: string | null;
  semAssinatura?: boolean;
  /** Admin do sistema: opera o SaaS, não assina plano — vê tudo. */
  verTudo: boolean;
}

const PESOS: Record<string, number> = { BRONZE: 1, PRATA: 2, OURO: 3 };

/**
 * O plano da creche logada, lido da ASSINATURA (`/saas/meu-plano`).
 *
 * Existe porque o painel lia `usuario.id_plano` — campo que **nunca existiu**
 * no token nem na entidade. Como `undefined` caía em `'BRONZE'`, toda creche
 * era tratada como Bronze: o menu escondia Funcionários, Financeiro, Cardápios,
 * Saúde e Nutrição de quem pagava Prata, e o Dashboard dava as boas-vindas ao
 * "plano BRONZE". O plano nunca esteve no usuário — ele é da creche.
 *
 * Um hook, e não a chamada solta em cada tela, para não haver duas versões da
 * mesma pergunta divergindo.
 */
export function usePlano() {
  const [dados, setDados] = useState<PlanoDaCreche | null>(null);
  const crecheSelecionada = lerCrecheSelecionada();

  useEffect(() => {
    let ativo = true;
    api.get<PlanoDaCreche>('/saas/meu-plano')
      .then(({ data }) => { if (ativo) setDados(data); })
      // Falha na consulta não pode esconder módulo de quem paga por ele:
      // mantém `null`, e quem consome trata isso como "não sei ainda".
      .catch(() => { if (ativo) setDados(null); });
    return () => { ativo = false; };
  }, [crecheSelecionada]);

  /**
   * O plano da creche alcança o mínimo exigido?
   *
   * Enquanto não se sabe (carregando ou falha), devolve `true`: mostrar um item
   * a mais por um instante é melhor que esconder de quem pagou por ele.
   */
  const alcanca = (minimo?: 'BRONZE' | 'PRATA' | 'OURO') => {
    if (dados?.verTudo) return true;
    if (!dados?.plano) return true;
    return (PESOS[dados.plano] ?? 1) >= (PESOS[minimo || 'BRONZE'] ?? 1);
  };

  return { plano: dados?.plano ?? null, status: dados?.status ?? null, verTudo: !!dados?.verTudo, alcanca };
}
